import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useCustomMindmap } from 'Custom-Mindmap';
import { toPng } from 'html-to-image';
import { MindMapContext } from '../../Context/contex';
import { templateService } from '../../Api/api';
import CompressionConfirmModal from '../ImageCompressionModal/CompressionConfirmModal';
import {
  dataUrlToFile,
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  uploadImageWithOptionalCompression,
} from '../../Utils/imageUpload';
import './TemplateThumbnail.css';

const createDefaultThumbnail = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 300;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 300, 200);
  ctx.fillStyle = '#cccccc';
  ctx.font = '24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Default Thumbnail', 150, 100);
  return canvas.toDataURL('image/png');
};

const TemplateThumbnail = ({ viewMode, onMenuClick } = {}) => {
  const { documentId: docID } = useParams();
  const { isTemplateDocument, templateData, setTemplateData, setSavingData } = MindMapContext();
  const { getViewport, setViewport, fitView } = useCustomMindmap();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [compressModalOpen, setCompressModalOpen] = useState(false);
  const [compressFileSize, setCompressFileSize] = useState(0);
  const compressChoiceRef = useRef(null);

  const existingThumbnail = useMemo(
    () => templateData?.thumbnail || createDefaultThumbnail(),
    [templateData?.thumbnail],
  );

  const promptForCompress = useCallback((largeFile, loadingKey) => {
    if (loadingKey) {
      message.destroy(loadingKey);
    }
    return new Promise((resolve, reject) => {
      setCompressFileSize(largeFile.size);
      compressChoiceRef.current = { resolve, reject };
      setCompressModalOpen(true);
    });
  }, []);

  const closeCompressModal = (choice) => {
    setCompressModalOpen(false);
    const pending = compressChoiceRef.current;
    compressChoiceRef.current = null;
    if (!pending) return;

    if (choice === 'cancel') {
      pending.reject(Object.assign(new Error('cancelled'), { isCancel: true }));
      return;
    }

    message.loading({
      content:
        choice === 'compress'
          ? 'Compressing and uploading image...'
          : 'Uploading original image...',
      key: 'upload',
    });
    pending.resolve(choice === 'compress');
  };

  const handleOpenModal = () => {
    setPreviewUrl(null);
    setCustomUrl(null);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    setIsCapturing(true);
    const previousViewport = getViewport();
    try {
      message.loading({ content: 'Capturing and uploading thumbnail...', key: 'capture' });
      const container = document.querySelector('.cm-root');
      if (!container) {
        message.error({ content: 'Mind map container not found', key: 'capture' });
        return;
      }

      await fitView({ padding: 0.08, duration: 0 });
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const { width, height } = container.getBoundingClientRect();
      const TEMPLATE_WIDTH = 1280;
      const TEMPLATE_HEIGHT = 720;
      const scale = Math.min(TEMPLATE_WIDTH / width, TEMPLATE_HEIGHT / height);

      const dataUrl = await toPng(container, {
        backgroundColor: '#ffffff',
        width: Math.round(width * scale),
        height: Math.round(height * scale),
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${width}px`,
          height: `${height}px`,
        },
        filter: (node) => {
          const cls = node.classList;
          if (!cls) return true;
          return (
            !cls.contains('cm-controls') &&
            !cls.contains('cm-minimap') &&
            !cls.contains('sideBarDragDropIcons') &&
            !cls.contains('mindMap-options')
          );
        },
      });

      const file = await dataUrlToFile(dataUrl, 'thumbnail.png');
      const cloudinaryUrl = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: (largeFile) => promptForCompress(largeFile, 'capture'),
        compressionOptions: { maxSide: 1280, quality: 0.8 },
      });
      setPreviewUrl(cloudinaryUrl);
      message.destroy('upload');
      message.success({ content: 'Thumbnail captured successfully!', key: 'capture' });
    } catch (error) {
      if (error?.isCancel) {
        message.destroy('capture');
        message.destroy('upload');
        return;
      }
      console.error('Failed to capture thumbnail:', error);
      message.destroy('upload');
      message.error({ content: error?.message || 'Failed to capture thumbnail', key: 'capture' });
    } finally {
      setViewport(previousViewport, { duration: 0 });
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    const thumbnailUrl = customUrl || previewUrl || templateData?.thumbnail;
    if (!thumbnailUrl) {
      message.warning('Please capture or upload a thumbnail first');
      return;
    }
    setIsSaving(true);
    try {
      const res = await templateService.updateTemplate({
        templateId: docID,
        data: { thumbnail: thumbnailUrl, docType: 'mileboard' },
        dispatch: setSavingData,
      });
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      if (res?.data?.data) {
        setTemplateData(res.data.data);
      } else {
        setTemplateData((prev) => ({ ...(prev || {}), thumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);
      if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
        message.loading({ content: 'Uploading image...', key: 'upload' });
      }
      const cloudinaryUrl = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: (largeFile) => promptForCompress(largeFile, 'upload'),
        compressionOptions: { maxSide: 1280, quality: 0.8 },
      });
      setCustomUrl(cloudinaryUrl);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      if (error?.isCancel) {
        message.destroy('upload');
        return;
      }
      console.error('Failed to upload image:', error);
      message.error({ content: error?.message || 'Failed to upload image', key: 'upload' });
    } finally {
      setIsUploading(false);
    }
    return false;
  };

  if (!isTemplateDocument) return null;

  const compressionModalZ = 2000;

  const modalContent = (
    <>
      <Modal
        title={(
          <div className="thumbnail-modal-header">
            <CameraOutlined className="thumbnail-modal-icon" />
            Thumbnail Preview
          </div>
        )}
        open={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleConfirmUpdate}
            className="update-thumbnail-btn"
            loading={isSaving}
          >
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl || existingThumbnail) ? (
              <img
                src={customUrl || previewUrl || existingThumbnail}
                alt="Thumbnail Preview"
                className="thumbnail-preview"
              />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
            >
              <Button
                icon={<UploadOutlined />}
                className="upload-btn"
                loading={isUploading}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Custom Thumbnail'}
              </Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button
              icon={<CameraOutlined />}
              onClick={handleCapture}
              className="capture-btn"
              loading={isCapturing}
              disabled={isCapturing || isUploading}
            >
              {isCapturing ? 'Capturing…' : 'Capture Current View'}
            </Button>
          </div>
        </div>
      </Modal>
      <CompressionConfirmModal
        open={compressModalOpen}
        fileSize={compressFileSize}
        onCompress={() => closeCompressModal('compress')}
        onUploadOriginal={() => closeCompressModal('original')}
        onCancel={() => closeCompressModal('cancel')}
        zIndex={compressionModalZ}
        getContainer={() => document.body}
      />
    </>
  );

  if (viewMode === 'menu') {
    return (
      <>
        <div
          className="thumbnail-menu-item"
          onClick={() => {
            handleOpenModal();
            if (onMenuClick) onMenuClick();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpenModal();
              if (onMenuClick) onMenuClick();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <CameraOutlined className="thumbnail-button-icon" />
          <span>Update Thumbnail</span>
        </div>
        {modalContent}
      </>
    );
  }

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      {modalContent}
    </>
  );
};

export default TemplateThumbnail;
